import { Contract, ContractsBusinessModel, Role, SuspendedReason } from '@ecm/portfolio';

import { StatusEnum } from '../../api/contract/status.enum';
import { TelcoMarketTypeEnum } from '../../api/contract/telcoMarketType.enum';
import { TelcoStateEnum } from '../../api/contract/telcoState.enum';
import { TelcoTypeEnum } from '../../api/contract/telcoType.enum';
import { BillSummaries } from '../../api/dashboard/billSummaries/billSummaries';
import { Dashboard } from '../../api/dashboard/dashboard';
import { Family } from '../../api/family/family';
import { Member } from '../../api/family/member';
import { DashboardReco } from '../../api/reco/dashboardReco';
import { ActiveContractViewModel } from '../../model/crossroads/contracts-options/active-contract.view-model';
import { MultiContractViewModel } from '../../model/crossroads/contracts-options/multi-contract.view-model';
import { ProtectedHouseContractViewModel } from '../../model/crossroads/contracts-options/protectedHouse-contract.view-model';
import { SuggestedContractViewModel } from '../../model/crossroads/contracts-options/suggested-contract.view-model';
import { TerminatedContractViewModel } from '../../model/crossroads/contracts-options/terminated-contract.view-model';
import { ContractsViewModel } from '../../model/crossroads/contracts.view-model';
import { CrossroadsViewModel } from '../../model/crossroads/crossroads.view-model';
import { TypeContractEnum } from '../../model/crossroads/typeContractEnum';
import { CrossroadsMapperUtils } from './crossroads-utils.mapper';

export class CrossroadsViewModelMapper {
    /**
     * Need to display family advantage for the first "open" contract
     */
    // TODO delete this comment line when display bug is fixed
    // private static canHaveFamilyAdvantages: boolean = true;
    private static canHaveFamilyAdvantages = false;

    /**
     * Need to calculate order to display sosh view or orange view
     */
    private static order = 0;

    /**
     * need to calculta if is family eligible
     */
    public static isFamilyEligible: boolean;

    /**
     * Parse API response to construct crossroads model
     * @param {dashboard} dashboard
     * @param {ContractsBusinessModel} contracts
     * @param {any} contractsAndOptionTranslations
     * @param {boolean} isFamilyEligible
     * @param {Family} family
     * @param {DashboardReco[]} dashboardsReco
     * @param {boolean} includeSuggested
     * @returns {CrossroadsViewModel}
     */
    public static getCrossroadsViewModel(
        dashboard: Dashboard,
        contracts: ContractsBusinessModel,
        contractsAndOptionTranslations: any,
        isFamilyEligible = false,
        family?: Family,
        dashboardsReco?: DashboardReco[],
        includeSuggested?: boolean,
    ): CrossroadsViewModel {
        const model: CrossroadsViewModel = new CrossroadsViewModel();
        this.isFamilyEligible = isFamilyEligible;

        if (!dashboard.forbidden) {
            model.forbidden = false;
            this.canHaveFamilyAdvantages = false;
            if (dashboard.universe) {
                model.universe = dashboard.universe;
            }
            let mapMembers: Map<string, Member> = new Map<string, Member>();
            if (family && Object.prototype.hasOwnProperty.call(family, 'members')) {
                mapMembers = CrossroadsMapperUtils.extractMapMembersFromFamily(family.members);
            }
            if (contracts) {
                const filteredContracts: Contract[] = contracts.contracts.filter((contract: Contract) => {
                    return (
                        !contract.telco ||
                        (!this.isFictifBalSecondaireContract(contract) &&
                            contract.telco.state &&
                            contract.telco.state !== TelcoStateEnum.NORMAL)
                    );
                });

                for (const contract of filteredContracts) {
                    this.extractContractByType(
                        contract,
                        model,
                        mapMembers,
                        dashboard,
                        contractsAndOptionTranslations,
                        dashboardsReco,
                        includeSuggested,
                    );
                }
            }
        }
        this.order = 0;
        return model;
    }

    /**
     * return true if "Fictif/BAL secondaire" contract
     * @param {Contract} contract
     * @return {boolean}
     */
    private static isFictifBalSecondaireContract(contract: Contract): boolean {
        return (
            contract.telco.state &&
            contract.telco.state === TelcoStateEnum.LOGGED &&
            contract.telco.marketType.toUpperCase() === TelcoMarketTypeEnum.RES &&
            contract.role &&
            contract.role.toUpperCase() === Role.UTILISATEUR
        );
    }

    /**
     * extract contract by type (main, suggested, multiType, terminated, protectedHouseContractViewModel)
     * @param contract
     * @param {CrossroadsViewModel} model
     * @param {Map<string, Member>} mapMembers
     * @param {Dashboard} dashboard
     * @param {any} contractsAndOptionTranslations
     * @param {DashboardReco[]}dashboardsReco
     * @param {boolean}includeSuggested
     */
    private static extractContractByType(
        contract,
        model: CrossroadsViewModel,
        mapMembers: Map<string, Member>,
        dashboard: Dashboard,
        contractsAndOptionTranslations: any,
        dashboardsReco: DashboardReco[],
        includeSuggested: boolean,
    ) {
        const crossroadsContracts: ContractsViewModel = new ContractsViewModel();
        if (contract.telco && contract.telco.state && contract.telco.state === TelcoStateEnum.SUGGESTED) {
            crossroadsContracts.contract = this.extractSuggestedContractViewModel(contract, mapMembers);
            if (includeSuggested) {
                crossroadsContracts.order = this.order;
                this.order++;
                crossroadsContracts.brand = contract.brand;
                model.contracts.push(crossroadsContracts);
            }
            model.suggestedContracts.push(crossroadsContracts.contract as SuggestedContractViewModel);
        } else {
            if (contract.telco && contract.telco.state && !contract.security) {
                this.fillTelcoContractType(
                    crossroadsContracts,
                    contract,
                    model,
                    mapMembers,
                    dashboard,
                    contractsAndOptionTranslations,
                    dashboardsReco,
                );
            } else if (
                contract.status === StatusEnum.ACTIVE ||
                contract.status === StatusEnum.TERMINATED ||
                contract.status === StatusEnum.RETRACTED
            ) {
                crossroadsContracts.contract = this.extractProtectedHouseContractViewModel(
                    contract,
                    contractsAndOptionTranslations,
                );
                crossroadsContracts.typeContract = TypeContractEnum.PROTECTEDHOUSE;
            }
            crossroadsContracts.order = this.order;
            this.order++;
            crossroadsContracts.brand = contract.brand;
            model.contracts.push(crossroadsContracts);
        }
    }

    /**
     * Fill contract type for telco contracts (terminated, multi, active, vim-eligible).
     * @param {ContractsViewModel} crossroadsContracts
     * @param contract
     * @param {CrossroadsViewModel} model
     * @param {Map<string, Member>} mapMembers
     * @param {Dashboard} dashboard
     * @param {any} contractsAndOptionTranslations
     * @param {DashboardReco[]} dashboardsReco
     */
    private static fillTelcoContractType(
        crossroadsContracts: ContractsViewModel,
        contract,
        model: CrossroadsViewModel,
        mapMembers: Map<string, Member>,
        dashboard: Dashboard,
        contractsAndOptionTranslations: any,
        dashboardsReco: DashboardReco[],
    ): void {
        if (contract.status === StatusEnum.CLOSED || contract.status === StatusEnum.TERMINATED) {
            crossroadsContracts.contract = this.extractTerminatedContractViewModel(
                contract,
                mapMembers,
                dashboard.embedded?.billSummaries,
            );
            crossroadsContracts.typeContract = TypeContractEnum.TERMINATED;
        } else if (contract.telco.marketType === 'OBS' || contract.telco.marketType === 'PRO') {
            crossroadsContracts.contract = this.extractMultiContractViewModel(
                contract,
                contract.telco.marketType,
                contractsAndOptionTranslations,
            );
            crossroadsContracts.typeContract = TypeContractEnum.MULTI;
        } else if (contract.telco.subType === 'parnasse') {
            crossroadsContracts.contract = this.extractMultiContractViewModel(
                contract,
                contract.telco.subType,
                contractsAndOptionTranslations,
            );
            crossroadsContracts.typeContract = TypeContractEnum.MULTI;
        } else {
            switch (contract.telco.state) {
                case TelcoStateEnum.LOGGED:
                case TelcoStateEnum.HELD:
                case TelcoStateEnum.DELEGATED:
                    crossroadsContracts.contract = this.extractActiveContractViewModel(
                        contract,
                        mapMembers,
                        dashboard.embedded?.billSummaries,
                        this.isFamilyEligible,
                        contractsAndOptionTranslations,
                        dashboardsReco,
                    );
                    crossroadsContracts.typeContract = TypeContractEnum.ACTIVE;
                    break;
                case TelcoStateEnum.VIM_ELIGIBLE:
                    model.isVimEligible = true;
                    break;
                default:
                    break;
            }
        }
    }

    /**
     * Extract contractProtectedHouseItemViewModel of contracts.
     * @param {Contract} contract
     * @param {any} contractsAndOptionTranslations
     * @return {ProtectedHouseContractViewModel}
     */
    private static extractProtectedHouseContractViewModel(
        contract: Contract,
        contractsAndOptionTranslations: any,
    ): ProtectedHouseContractViewModel {
        if (contractsAndOptionTranslations) {
            const translation: any = contractsAndOptionTranslations['protectedHouse'];
            const protectedHouseContractViewModel: ProtectedHouseContractViewModel = Object.assign(
                new ProtectedHouseContractViewModel(),
                {
                    cid: contract.cid,
                    offerName: contract.offerName,
                    detail: translation['detail'].concat(' ', contract.cid),
                    picto: contract.brand.toUpperCase(),
                },
            );
            if (contract.status === StatusEnum.ACTIVE) {
                protectedHouseContractViewModel.messages = translation['message'];
                protectedHouseContractViewModel.button = translation['button'];
                protectedHouseContractViewModel.linkInternet = translation['link'];
            }
            if (contract.status === StatusEnum.TERMINATED) {
                protectedHouseContractViewModel.offerName = translation['suspendedOfferName'];
                protectedHouseContractViewModel.activeContract = false;
            }
            if (contract.status === StatusEnum.RETRACTED) {
                protectedHouseContractViewModel.offerName = translation['retractedOfferName'];
                protectedHouseContractViewModel.activeContract = false;
            }
            return protectedHouseContractViewModel;
        }
    }

    /**
     * Extract suggestedContractViewModel of contract
     * @param {Contract} contract
     * @param {any} mapMembers
     * @return {ActiveContractViewModel} contractItemViewModel
     */
    private static extractSuggestedContractViewModel(
        contract: Contract,
        mapMembers: Map<string, Member>,
    ): SuggestedContractViewModel {
        return Object.assign(new SuggestedContractViewModel(), {
            offerName: contract.offerName,
            detail: CrossroadsMapperUtils.getContractDetail(
                contract.telco.type,
                contract.telco.subType,
                contract.telco.publicNumber,
            ),
            cid: contract.cid,
            state: contract.telco.state.toString(),
            type: contract.telco.type.toString(),
            brand: contract.brand,
            member: CrossroadsMapperUtils.getMemberDisplayName(mapMembers, contract.cid),
        });
    }

    /**
     * extract terminated contracts
     * @param {Contract} contract
     * @param {Map<string, Member>} mapMembers
     * @param {BillSummaries[]} billSummaries
     * @return {TerminatedContractViewModel}
     */
    private static extractTerminatedContractViewModel(
        contract: Contract,
        mapMembers?: Map<string, Member>,
        billSummaries?: BillSummaries[],
    ): TerminatedContractViewModel {
        const terminatedContractViewModel: TerminatedContractViewModel = Object.assign(
            new TerminatedContractViewModel(),
            {
                cid: contract.cid,
                brand: contract.brand,
                offerName: contract.offerName,
                picto: contract.brand.toUpperCase(),
                detail: CrossroadsMapperUtils.getContractDetail(
                    contract.telco.type,
                    contract.telco.subType,
                    contract.telco.publicNumber,
                ),
            },
        );
        if (mapMembers) {
            terminatedContractViewModel.member = CrossroadsMapperUtils.getMemberDisplayName(mapMembers, contract.cid);
        }
        if (billSummaries) {
            terminatedContractViewModel.billItem = CrossroadsMapperUtils.getBillItem(billSummaries, contract.cid);
        }
        return terminatedContractViewModel;
    }

    /**
     * extract contract list for multi contract (OBS,PRO,parnasse)
     * @param {Contract} contract
     * @param {string} contractType
     * @param {any} contractsAndOptionTranslations
     * @return {MultiContractViewModel}
     */
    private static extractMultiContractViewModel(
        contract: Contract,
        contractType?: string,
        contractsAndOptionTranslations?: any,
    ): MultiContractViewModel {
        const multiContractViewModel: MultiContractViewModel = Object.assign(new MultiContractViewModel(), {
            cid: contract.cid,
            brand: contract.brand,
            offerName: contract.offerName,
            detail: CrossroadsMapperUtils.getContractDetail(
                contract.telco.type,
                contract.telco.subType,
                contract.telco.publicNumber,
            ),
        });
        if (contractType && contractsAndOptionTranslations) {
            const translation: any = contractsAndOptionTranslations['multiContract'];
            multiContractViewModel.button = translation['button'];
            multiContractViewModel.messages = translation[contractType.toLocaleLowerCase()]['message'];
            multiContractViewModel.linkInternet = translation[contractType.toLocaleLowerCase()]['link'];
            if (contractType.toLocaleLowerCase() === 'parnasse') {
                multiContractViewModel.eSimLink = translation['parnasse']['eSimLink'];
            }
        }
        return multiContractViewModel;
    }

    /**
     * Extract contractItemViewModel of contract and mapMembers.
     * @param {Contract} contract
     * @param {Map<string, Member>} mapMembers
     * @param {BillSummaries[]} billSummaries
     * @param {boolean} isFamilyEligible
     * @param {any} contractsAndOptionTranslations
     * @param {DashboardReco[]} dashboardsReco
     * @return {ActiveContractViewModel}
     */
    private static extractActiveContractViewModel(
        contract: Contract,
        mapMembers: Map<string, Member>,
        billSummaries: BillSummaries[],
        isFamilyEligible: boolean,
        contractsAndOptionTranslations?: any,
        dashboardsReco?: DashboardReco[],
    ): ActiveContractViewModel {
        const activeContractViewModel: ActiveContractViewModel = Object.assign(new ActiveContractViewModel(), {
            brand: contract.brand,
            offerName: contract.offerName,
            detail: CrossroadsMapperUtils.getContractDetail(
                contract.telco.type,
                contract.telco.subType,
                contract.telco.publicNumber,
            ),
            cid: contract.cid,
            role: contract.role,
            type: contract.telco.type.toString(),
            state: contract.telco.state.toString(),
            member: CrossroadsMapperUtils.getMemberDisplayName(mapMembers, contract.cid),
            billItem: CrossroadsMapperUtils.getBillItem(billSummaries, contract.cid),
            picto: contract.brand.toUpperCase(),
        });
        if (Object.prototype.hasOwnProperty.call(contract, 'contractInstallationArea')) {
            activeContractViewModel.installationCity = contract.contractInstallationArea.city;
        }
        if (dashboardsReco && dashboardsReco.length > 0) {
            activeContractViewModel.contractReco = CrossroadsMapperUtils.getContractReco(dashboardsReco, contract.cid);
        }
        if (contract.koType && contractsAndOptionTranslations) {
            activeContractViewModel.koContract = CrossroadsMapperUtils.getKoContract(
                contract.koType,
                contractsAndOptionTranslations['isKoContract'],
            );
        }
        if (contract.status === StatusEnum.INPROGRESS) {
            activeContractViewModel.koContract = CrossroadsMapperUtils.getInprogressContract(
                contract.brand,
                contractsAndOptionTranslations['inprogressContract'],
            );
            if (TelcoTypeEnum[activeContractViewModel.type] === TelcoTypeEnum.mobilePostpaid) {
                activeContractViewModel.detail = null;
            }
        }
        // Checks for unpaid contracts and provide suspendedContract object
        if (
            (contract.telco.suspendedReason &&
                (contract.telco.suspendedReason === SuspendedReason.LOSS ||
                    contract.telco.suspendedReason === SuspendedReason.THEFT)) ||
            CrossroadsMapperUtils.isUnpaidBill(contract.cid, billSummaries)
        ) {
            activeContractViewModel.suspendedContract = CrossroadsMapperUtils.getSuspendedViewModel(
                contract,
                contractsAndOptionTranslations['suspendedContract'],
                billSummaries,
            );
        }
        if (contract.role && contract.role.toLocaleUpperCase() !== Role.TITULAIRE) {
            activeContractViewModel.delegation = contract.holder.lastName;
            if (contract.holder.firstName) {
                activeContractViewModel.delegation = contract.holder.firstName.concat(
                    ' ',
                    activeContractViewModel.delegation,
                );
            }
        }
        // if canHaveFamilyAdvantages is false set hasFamilyAdvantages at true
        if (
            !this.canHaveFamilyAdvantages &&
            TelcoTypeEnum[contract.telco.type] === TelcoTypeEnum.open &&
            (contract.telco.state === TelcoStateEnum.LOGGED || contract.telco.state === TelcoStateEnum.HELD)
        ) {
            activeContractViewModel.hasFamilyAdvantages = true;
            activeContractViewModel.familyAdvantages = CrossroadsMapperUtils.getFamilyAdvantages(
                contract,
                contractsAndOptionTranslations['familyAdvantages'],
            );
            // can only have one advantage for a page
            this.canHaveFamilyAdvantages = true;
        }

        if (isFamilyEligible) {
            activeContractViewModel.isFamilyEligible = isFamilyEligible;
        }
        return activeContractViewModel;
    }
}
